import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { HandPointer } from '@components/Intro/HandPointer/HandPointer.tsx';
import cn from 'classnames';

import { newIntroSteps } from '@/common/config/intro-steps';
import { getCssVar } from '@/common/lib/cssVar';

// import { Picture } from '@/components/ui/Picture/Picture';
import { IntroContext } from '../context';
import { FocusAnimation } from '../FocusAnimation/FocusAnimation';
import { useIntroClickHandler } from '../hooks/intro-click-handler';
import CharacterPicture from '../Step/CharacterPicture';
import { Step } from '../Step/Step';

import css from './styles.module.scss';

interface RectProp {
  left: number;
  top: number;
  width: number;
  height: number;
}

const Overlay: FC = () => {
  const { activeStep, toNextStep, highlightComponentRect } =
    useContext(IntroContext);
  const { t } = useTranslation();
  useIntroClickHandler();

  const getPointerPositionFromRect = (
    rect: RectProp | null,
    activeStep: number,
  ): { x: number; y: number } | null => {
    if (!rect) return null;

    const offsets: Record<number, { x?: number; y?: number }> = {
      2: { x: 50 },
      3: { x: 50 },
      5: { x: 50, y: 70 },
      7: { x: 50 },
      9: { x: 50 },
      10: { x: 50 },
      14: { x: -35 },
      23: { x: -30 },
    };

    const offset = offsets[activeStep] ?? {};

    const desktopContainerOffsetLeft = parseFloat(
      getCssVar('--desktop-container-offset-left'),
    );

    return {
      x:
        rect.left +
        rect.width / 2 +
        (offset.x ?? 0) -
        desktopContainerOffsetLeft,
      y: rect.top + rect.height / 2 + (offset.y ?? 0),
    };
  };

  const position = getPointerPositionFromRect(
    highlightComponentRect,
    activeStep,
  );
  const activeStepConfig = newIntroSteps[activeStep];
  const { text } = newIntroSteps[activeStep];

  return (
    <div className={cn(css.overlay)}>
      <FocusAnimation hasCharacter={!!activeStepConfig?.character} />
      <Step
        currentStep={activeStep}
        title={t(activeStepConfig.title || '')}
        text={t(activeStepConfig.text)}
        buttonText={t(activeStepConfig.buttonText)}
        withNextButton={activeStepConfig.withNextButton}
        withAdditionalButton={activeStepConfig.withAdditionalButton}
        toNextStep={toNextStep}
        config={activeStepConfig}
      />
      {!!text && <CharacterPicture config={activeStepConfig} />}
      {!activeStepConfig?.withoutHand &&
        highlightComponentRect &&
        !!position?.x &&
        !!position?.y && (
          <HandPointer
            className={cn(
              'absolute',
              activeStep === 5 ? 'rotate-[-15deg] w-[104px]' : 'w-[72px]',
            )}
            style={{ left: position?.x ?? 0, top: position?.y ?? 0 }}
          />
        )}
    </div>
  );
};

export { Overlay };
